import type {
  ActiveSpeaker,
  ConnectionChangePayload,
  Participant,
  ParticipantPropertiesPayload,
} from "@zoom/videosdk";
import { ConnectionState } from "@zoom/videosdk";
import { RoomEvent, RoomEventHandlerMap } from "../../core/room/types";
import { handleUnhandledEventHandlerException } from "../../core/utils";
import { VideoClient } from "../types";
import { ZoomVideoClientEventHandlerMap } from "../VideoClientEvents";
import { ZoomRemoteParticipant } from "./participant/RemoteParticipant";
import { ZoomRoom } from "./RoomDelegator";
import { noop } from "../../../utils";

export type ZoomRoomEvent =
  | "user-added"
  | "user-updated"
  | "user-removed"
  | "connection-change"
  | "active-speaker";

export type ZoomRoomEventHandlerMap = Pick<
  ZoomVideoClientEventHandlerMap,
  ZoomRoomEvent
>;

export type ZoomRoomEventHandler<K extends ZoomRoomEvent> =
  ZoomRoomEventHandlerMap[K];

/**
 * Contains the list of actively used standard events mapped to Zoom events.
 */
export const STANDARD_ROOM_EVENT_TO_ZOOM_ROOM_EVENT_MAP: Record<
  RoomEvent,
  ZoomRoomEvent
> = {
  participantConnected: "user-added",
  participantReconnecting: "user-updated",
  participantReconnected: "user-added",
  participantDisconnected: "user-removed",
  disconnected: "connection-change",
  reconnecting: "connection-change",
  reconnected: "connection-change",
  dominantSpeakerChanged: "active-speaker",
} as const;

export type StandardRoomEventToZoomRoomEventMap =
  typeof STANDARD_ROOM_EVENT_TO_ZOOM_ROOM_EVENT_MAP;

/**
 * Type that maps each RoomEvent to its corresponding factory function signature
 */
type EventConfigFactoryMap = {
  [K in RoomEvent]: (
    videoClient: VideoClient,
    handler: RoomEventHandlerMap[K],
  ) => ZoomVideoClientEventHandlerMap[StandardRoomEventToZoomRoomEventMap[K]];
};

/**
 * The {@link ParticipantPropertiesPayload} is the same as {@link Participant}
 * but with all optional properties. This function converts
 * {@link ParticipantPropertiesPayload} to {@link Participant} by providing
 * default values for missing properties.
 */
function participantPropertiesToParticipant(
  payload: ParticipantPropertiesPayload,
): Participant {
  return {
    ...payload,
    audio: payload.audio ?? "",
    bVideoOn: payload.bVideoOn ?? false,
    displayName: payload.displayName ?? "",
    isHost: payload.isHost ?? false,
    isManager: payload.isManager ?? false,
    isVideoConnect: payload.isVideoConnect ?? false,
    sharerOn: payload.sharerOn ?? false,
    sharerPause: payload.sharerPause ?? false,
  };
}

/**
 * Filters out the local participant from "user-added" events. After network
 * disruption, Zoom's server may still have the old participant instance as a
 * zombie. Since userId is regenerated per connection but userIdentity is stable,
 * we filter by userIdentity to exclude the current user (and zombie instances).
 */
function createParticipantConnectedHandler(
  videoClient: VideoClient,
  handler: RoomEventHandlerMap["participantConnected"],
) {
  return function handleUserAdded(payload: ParticipantPropertiesPayload[]) {
    const currentUserIdentity = videoClient.getCurrentUserInfo()?.userIdentity;
    const participantIdentities = new Set(payload.map((p) => p.userIdentity));
    const participants = videoClient.getAllUser().filter((p) => {
      const { userIdentity } = p;
      return (
        participantIdentities.has(userIdentity) &&
        userIdentity !== currentUserIdentity
      );
    });
    for (const participant of participants) {
      handler(new ZoomRemoteParticipant(participant, videoClient));
    }
  };
}

function createParticipantReconnectingHandler(
  videoClient: VideoClient,
  handler: RoomEventHandlerMap["participantReconnecting"],
) {
  return function handleUserReconnecting(
    payload: ParticipantPropertiesPayload[],
  ) {
    for (const participantProperties of payload) {
      let participant: Participant | undefined;
      if (
        participantProperties.isInFailover &&
        (participant = videoClient.getUser(participantProperties.userId))
      ) {
        handler(new ZoomRemoteParticipant(participant, videoClient));
      }
    }
  };
}

/**
 * Zoom does not have a specific event for participant reconnected. Users either
 * seemlessly reconnect behind the scenes without an event or fail to and enter
 * a failover state. When a user is in a failover state, we consider them to be
 * reconnecting, but they can only be reconnected once they fully rejoin the
 * session. In other words, rejoining will trigger the
 * user-added/participant-connected handlers as a new remote participant
 * (different SID) which should be equipped to handle rejoins.
 */
function createParticipantReconnectedHandler(
  _videoClient: VideoClient,
  _handler: RoomEventHandlerMap["participantReconnected"],
) {
  return noop;
}

function createParticipantDisconnectedHandler(
  videoClient: VideoClient,
  handler: RoomEventHandlerMap["participantDisconnected"],
) {
  return function handleUserRemoved(payload: ParticipantPropertiesPayload[]) {
    for (const participantProperties of payload) {
      handler(
        new ZoomRemoteParticipant(
          participantPropertiesToParticipant(participantProperties),
          videoClient,
        ),
      );
    }
  };
}

// Helper functions for connection events
function createDisconnectedHandler(
  videoClient: VideoClient,
  handler: RoomEventHandlerMap["disconnected"],
) {
  return function handleDisconnected(payload: ConnectionChangePayload) {
    const { state, reason } = payload;
    // The reason type is misleading - it's also set to "connection error" on
    // failure
    const disconnectError =
      state === ConnectionState.Fail ? new Error(reason) : undefined;
    if (state === ConnectionState.Closed || disconnectError) {
      const result = handler(new ZoomRoom(videoClient), disconnectError, {
        // Failover state, must manually rejoin
        // https://devforum.zoom.us/t/how-to-handle-internet-disconnection-properly/65018
        isRoomRejoinable: state === ConnectionState.Fail,
      });
      handleUnhandledEventHandlerException(result, "Zoom: disconnected");
    }
  };
}

function createReconnectingHandler(
  _videoClient: VideoClient,
  handler: RoomEventHandlerMap["reconnecting"],
) {
  return function handleReconnecting(payload: ConnectionChangePayload) {
    const { state, reason } = payload;
    if (state === ConnectionState.Reconnecting) {
      const error = new Error(
        `Connection changed to state: ${state}, reason: ${reason}`,
      );
      handler(error);
    }
  };
}

function createReconnectedHandler(
  _videoClient: VideoClient,
  handler: RoomEventHandlerMap["reconnected"],
) {
  return function handleReconnected(payload: ConnectionChangePayload) {
    const { state } = payload;
    if (state === ConnectionState.Connected) {
      handler();
    }
  };
}

// Helper function for dominant speaker events
function createDominantSpeakerChangedHandler(
  videoClient: VideoClient,
  handler: RoomEventHandlerMap["dominantSpeakerChanged"],
) {
  return function handleActiveSpeaker(activeSpeakers: ActiveSpeaker[]) {
    if (activeSpeakers.length > 0) {
      const participant = videoClient.getUser(activeSpeakers[0].userId);
      if (participant) {
        handler(new ZoomRemoteParticipant(participant, videoClient));
      }
    }
  };
}

export const EVENT_CONFIG_MAP: EventConfigFactoryMap = {
  participantConnected: createParticipantConnectedHandler,
  participantReconnecting: createParticipantReconnectingHandler,
  participantReconnected: createParticipantReconnectedHandler,
  participantDisconnected: createParticipantDisconnectedHandler,
  disconnected: createDisconnectedHandler,
  reconnecting: createReconnectingHandler,
  reconnected: createReconnectedHandler,
  dominantSpeakerChanged: createDominantSpeakerChangedHandler,
} as const;
